<template>
  <div v-if="!catReady" class="loading-container">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading CAT parameters...</span>
    </div>
    <p class="mt-3">🐈 Initializing Adaptive Test...</p>
  </div>

  <div v-else-if="catReady && catEngine && currentQuestion && !testCompleted" class="adaptive-test-wrapper">
    <!-- Bandeau mode preview -->
    <div v-if="previewMode" class="preview-banner">
      <i class="fas fa-eye me-2"></i>
      <strong>MODE APERÇU</strong> - Test CAT en prévisualisation (les résultats ne sont pas sauvegardés)
    </div>
    
    <!-- TestRenderer with navbar at full width -->
    <TestRenderer
          :testData="currentQuestionData"
          :questionIndexMap="questionIndexMap"
          :readOnly="false"
          :visitedPages="visitedPages"
          :lockedQuestions="lockedQuestions"
          :showTestMap="false"
          :currentPageIndex="0"
          :lastPageReached="lastPageReached"
          :answers="answers"
          :stopTimer="stopTimer"
          :hideNavigation="true"
          :onPrevPage="() => {}"
          :onNextPage="() => {}"
          :onSendResults="afterCATTest"
          :handleNavigateToPage="() => {}"
          @time-up="onTimeUp"
          @toggle-test-map="() => {}"
          @lock-question="handleLockQuestion"
        />
    
    <!-- CAT Progress and Controls in separate container -->
    <div class="adaptive-test-container">
      <!-- CAT Progress Indicator -->
      <div class="cat-progress-bar">
        <div class="progress mb-3">
          <div 
            class="progress-bar bg-info" 
            role="progressbar" 
            :style="{ width: progressPercentage + '%' }"
            :aria-valuenow="progressPercentage"
            aria-valuemin="0" 
            aria-valuemax="100"
          >
            {{ Math.round(progressPercentage) }}%
          </div>
        </div>
        <div class="cat-status">
          <small class="text-muted">
            🐈 Question {{ currentQuestionIndex + 1 }} | 
            Level {{ currentLevel }} | 
            Ability: {{ currentAbilityLevel.toFixed(2) }}
          </small>
        </div>
      </div>
        
      <!-- CAT Navigation - Directly under the question -->
      <div class="cat-navigation-inline">
        <button 
          v-if="!canFinishTest"
          class="btn btn-primary btn-lg"
          @click="handleNextQuestion"
          :disabled="!isCurrentQuestionAnswered()"
        >
          <i class="fas fa-arrow-right me-2"></i>
          Next Question
        </button>
        
        <!-- Message de fin automatique au lieu d'un bouton -->
        <div v-else class="test-completion-message">
          <div class="alert alert-success text-center">
            <i class="fas fa-check-circle fa-2x mb-2"></i>
            <h4>🎉 Evaluation Completed!</h4>
            <p>Your level has been determined. Results will be saved automatically.</p>
            <div class="spinner-border spinner-border-sm me-2" role="status"></div>
            <span>Saving results...</span>
          </div>
        </div>
      </div>
          
      <!-- CAT Debug Panel - Affiché seulement en mode formative pendant le test -->
      <div v-if="catEngine && isFormativeMode" class="cat-debug-panel">
        <div class="debug-header">
          <h5>
            <i class="fas fa-chart-line me-2"></i>
            CAT Performance Dashboard
          </h5>
          <small class="text-muted">Live adaptive testing metrics</small>
        </div>
        
        <div class="row">
          <!-- Current Status -->
          <div class="col-md-4">
            <div class="debug-card">
              <h6><i class="fas fa-crosshairs me-1"></i> Current Status</h6>
              <div class="status-item">
                <span class="label">Current Level:</span>
                <span class="value level-badge-mini" :class="'level-' + currentLevel">{{ currentLevel }}</span>
              </div>
              <div class="status-item">
                <span class="label">Question Difficulty:</span>
                <span class="value">{{ currentQuestion?.difficulty || 'Auto-assigned' }}</span>
              </div>
              <div class="status-item">
                <span class="label">Questions Answered:</span>
                <span class="value">{{ catEngine.getCurrentState().questionsAnswered }}</span>
              </div>
            </div>
          </div>
          
          <!-- Progress Tracking -->
          <div class="col-md-4">
            <div class="debug-card">
              <h6><i class="fas fa-trophy me-1"></i> Current Level Progress</h6>
              <div class="status-item">
                <span class="label">Level {{ currentLevel }} Successes:</span>
                <span class="value text-success">{{ currentLevelStats.successes }}</span>
                <span class="threshold">/ {{ catEngine?.confirmationThreshold || 3 }}</span>
              </div>
              <div class="status-item">
                <span class="label">Level {{ currentLevel }} Failures:</span>
                <span class="value text-danger">{{ currentLevelStats.failures }}</span>
                <span class="threshold" v-if="currentLevel === 1">/ {{ catEngine?.failureThreshold || 3 }}</span>
              </div>
              <div class="status-item">
                <span class="label">Global Success Rate:</span>
                <span class="value">{{ globalSuccessRate }}%</span>
              </div>
            </div>
          </div>
          
          <!-- Completion Status -->
          <div class="col-md-4">
            <div class="debug-card">
              <h6><i class="fas fa-flag-checkered me-1"></i> Completion Status</h6>
              <div class="status-item">
                <span class="label">Confirmation Threshold:</span>
                <span class="value">{{ catEngine.confirmationThreshold }}</span>
              </div>
              <div class="status-item">
                <span class="label">Failure Threshold:</span>
                <span class="value text-warning">{{ catEngine.failureThreshold }}</span>
              </div>
              <div class="status-item completion-indicator">
                <span class="label">Test Status:</span>
                <span class="value" :class="getCompletionStatusClass()">
                  {{ getCompletionStatus() }}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Level Breakdown Visualization -->
        <div class="level-breakdown">
          <h6><i class="fas fa-layer-group me-1"></i> Level Performance Breakdown</h6>
          <div class="levels-grid">
            <div 
              v-for="(levelData, index) in getLevelBreakdown()" 
              :key="index" 
              class="level-bar-container"
            >
              <div class="level-header">
                <span class="level-number">L{{ index + 1 }}</span>
                <span class="level-difficulty">{{ getDifficultyLabel(index) }}</span>
              </div>
              <div class="level-bar">
                <div class="success-bar" :style="{ width: getSuccessPercentage(levelData) + '%' }"></div>
                <div class="failure-bar" :style="{ width: getFailurePercentage(levelData) + '%' }"></div>
              </div>
              <div class="level-stats">
                <span class="text-success">✓{{ levelData.successes }}</span>
                <span class="text-danger">✗{{ levelData.failures }}</span>
                <span class="text-muted">({{ levelData.questionsAttempted }} total)</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Warning Messages -->
        <div v-if="getWarningMessage()" class="warning-section">
          <div class="alert alert-warning">
            <i class="fas fa-exclamation-triangle me-2"></i>
            {{ getWarningMessage() }}
          </div>
        </div>
      </div>

      <!-- CAT Validation Status -->
      <div v-if="levelValidationStatus.length > 0" class="validation-status mt-3">
        <h5 class="mb-3">🎯 Competency Validation Status</h5>
        <div class="row">
          <div 
            v-for="levelStatus in levelValidationStatus" 
            :key="levelStatus.level"
            class="col-md-2 mb-2"
          >
            <div class="level-badge" :class="{
              'validated': levelStatus.validated,
              'in-progress': levelStatus.correctCount > 0 && !levelStatus.validated,
              'not-started': levelStatus.correctCount === 0
            }">
              <div class="level-number">{{ levelStatus.level }}</div>
              <div class="level-progress">{{ levelStatus.correctCount }}/{{ catEngine?.confirmationThreshold || 3 }}</div>
              <i v-if="levelStatus.validated" class="fas fa-check-circle validation-icon"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <CATFinalPage 
    v-else-if="testCompleted"
    :testData="testData"
    :answers="answers"
    :stopTimer="stopTimer"
    :finalAbilityLevel="finalAbilityLevel"
    :answeredQuestions="answeredQuestions"
    :levelValidationStatus="levelValidationStatus"
    :confirmationThreshold="catEngine?.confirmationThreshold || 3"
    :savingStatus="savingStatus"
    :saveError="saveError"
    @time-up="onTimeUp"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/api';
import { useResponsesStore } from '@/stores/useResponsesStore';
import { usePublicationStore } from '@/stores/usePublicationStore';
import TestRenderer from './TestRenderer.vue';
import CATFinalPage from './CATFinalPage.vue';
import { useSessionManager } from '@/testRenderUtil/useSessionManager';
import { useResultSender } from '@/testRenderUtil/useResultSender';
import { CATEngine } from '@/utils/catEngine.js';

// Props pour le mode preview
const props = defineProps({
  testData: Object,
  previewMode: {
    type: Boolean,
    default: false
  },
  readOnly: {
    type: Boolean,
    default: false
  }
});

// Basic setup
const route = useRoute();
const publicationStore = usePublicationStore();
const responsesStore = useResponsesStore();

const testId = ref(route.query.testId);
const publicationId = route.query.publicationId;
const groupId = route.query.groupId;
const stopTimer = ref(false);
const lastPageReached = ref(false);
const questionIndexMap = ref({});
const visitedPages = ref(new Set());
const lockedQuestions = ref(new Set());

// CAT-specific state
const catReady = ref(false);
const catEngine = ref(null);
const testData = ref(null);
const currentQuestion = ref(null);
const currentQuestionIndex = ref(0);
const testCompleted = ref(false);
const finalReport = ref(null);

// Result saving state
const savingStatus = ref('saving'); // 'saving', 'success', 'error'
const saveError = ref('');

// Get user token
const token = localStorage.getItem('token');
let userId = null;
if (token) {
  try {
    const decodedToken = JSON.parse(atob(token.split('.')[1]));
    userId = decodedToken?._id || decodedToken?.sub;
  } catch (e) {
    console.error("Token decoding failed:", e);
  }
}

let sessionManager = null;
const { sendResultsToDatabase } = useResultSender(responsesStore);

// Computed properties
const answers = computed(() => {
  return responsesStore.responses.reduce((acc, r) => {
    acc[r.questionId] = r.answered;
    return acc;
  }, {});
});

const progressPercentage = computed(() => {
  if (!catEngine.value) return 0;
  const state = catEngine.value.getCurrentState();
  // Progression basée sur le nombre de confirmations obtenues
  const confirmationThreshold = catEngine.value.confirmationThreshold;
  return Math.min((state.totalSuccesses / confirmationThreshold) * 100, 100);
});

const canFinishTest = computed(() => {
  if (!catEngine.value) return false;
  return catEngine.value.isTestCompleted();
});

const currentLevel = computed(() => {
  if (!catEngine.value) return 1;
  return catEngine.value.currentLevel;
});

const currentAbilityLevel = computed(() => {
  if (!catEngine.value) return 0;
  const state = catEngine.value.getCurrentState();
  // Estimation simple basée sur le ratio de réussites
  const totalQuestions = state.questionsAnswered;
  if (totalQuestions === 0) return 0;
  return (state.totalSuccesses / totalQuestions) * currentLevel.value;
});

const currentLevelStats = computed(() => {
  if (!catEngine.value) return { successes: 0, failures: 0 };
  return catEngine.value.getCurrentLevelStats();
});

const globalSuccessRate = computed(() => {
  if (!catEngine.value) return 0;
  const state = catEngine.value.getCurrentState();
  if (state.questionsAnswered === 0) return 0;
  return Math.round((state.totalSuccesses / state.questionsAnswered) * 100);
});

const publicationMode = computed(() => {
  return publicationStore.publicationData?.mode || 'EXAM';
});

const isFormativeMode = computed(() => {
  const mode = publicationMode.value?.toLowerCase();
  const isFormative = mode === 'learning' || mode === 'formative';
  console.log('🔍 CAT Debug Panel - Mode detected:', {
    originalMode: publicationMode.value,
    normalizedMode: mode,
    isFormative,
    showDebugPanel: isFormative
  });
  return isFormative;
});

const answeredQuestions = computed(() => {
  if (!catEngine.value) return [];
  return catEngine.value.questionHistory;
});

const finalAbilityLevel = computed(() => {
  if (!catEngine.value) return 0;
  
  // Si le test est terminé et qu'on a un rapport final, utiliser le niveau final
  if (testCompleted.value && finalReport.value) {
    return finalReport.value.finalLevel || currentLevel.value;
  }
  
  // Sinon utiliser le calcul en temps réel
  return currentAbilityLevel.value;
});

const currentQuestionData = computed(() => {
  if (!currentQuestion.value) return null;
  
  // Create a single-question test structure for TestRenderer
  return {
    title: testData.value.title,
    pages: [{
      id: "page_0",
      label: "Question",
      children: [{
        id: currentQuestion.value.el_ID,
        label: "Question"
      }]
    }],
    elements: [currentQuestion.value]
  };
});

// CAT Functions
const fetchCATData = async () => {
  try {
    console.log('🐈 Fetching CAT data for test:', testId.value);
    
    // Fetch test with CAT parameters
    const testResponse = await api.get(`/tests/${testId.value}`);
    testData.value = testResponse.data;
    
    console.log('🔍 Test response:', testData.value);
    
    // Vérifications défensives avec logs détaillés
    if (!testData.value) {
      console.error('❌ testData.value is null or undefined');
      throw new Error('No test data received from API');
    }
    
    if (!testData.value.elements) {
      console.error('❌ testData.value.elements is undefined');
      console.log('📋 Available properties in testData:', Object.keys(testData.value));
      throw new Error('Test data incomplete: elements property missing');
    }
    
    if (!Array.isArray(testData.value.elements)) {
      console.error('❌ testData.value.elements is not an array:', typeof testData.value.elements);
      throw new Error('Test data incomplete: elements is not an array');
    }
    
    if (testData.value.elements.length === 0) {
      console.warn('⚠️ No elements found in test data');
      throw new Error('Test data incomplete: no elements found');
    }
    
    // Log de l'état des données CAT
    console.log('📊 CAT data status:', {
      hasLevels: !!(testData.value.levels && testData.value.levels.length > 0),
      hasElements: !!(testData.value.elements && testData.value.elements.length > 0),
      hasCATParameters: !!(testData.value.CATParameters),
      elementsCount: testData.value.elements.length,
      levelsCount: testData.value.levels?.length || 0
    });

    // Initialiser le moteur CAT avec les données du test
    catEngine.value = new CATEngine(testData.value);
    
    // Obtenir la première question
    currentQuestion.value = catEngine.value.getNextQuestion();
    
    if (!currentQuestion.value) {
      throw new Error('Unable to get first question from CAT engine');
    }
    
    console.log('✅ CAT engine initialized successfully:', {
      startingLevel: catEngine.value.currentLevel,
      totalLevels: catEngine.value.levels.length,
      totalElements: testData.value.elements.length,
      firstQuestion: currentQuestion.value.el_ID
    });
    
    catReady.value = true;
    
  } catch (error) {
    console.error('❌ Error fetching CAT data:', error);
    alert('Error loading adaptive test configuration: ' + error.message);
  }
};

// Vérifie si la question actuelle a été répondue
const isCurrentQuestionAnswered = () => {
  if (!currentQuestion.value) return false;
  const response = responsesStore.responses.find(r => r.questionId === currentQuestion.value.el_ID);
  return response && response.answered !== undefined && response.answered !== null;
};

// Traite la réponse et passe à la question suivante
const handleNextQuestion = async () => {
  if (!isCurrentQuestionAnswered() || !catEngine.value) {
    console.warn('⚠️ Cannot proceed: question not answered or CAT engine not ready');
    return;
  }

  // Récupérer la réponse de l'étudiant
  const response = responsesStore.responses.find(r => r.questionId === currentQuestion.value.el_ID);
  
  console.log('🔍 Raw response from store:', {
    questionId: currentQuestion.value.el_ID,
    fullResponse: response,
    answeredValue: response?.answered,
    answerScore: response?.score,
    maxScore: response?.maxScore,
    mark: response?.score
  });
  
  // ✅ CORRIGÉ: Utiliser le score déjà calculé par le composant ChoiceRender
  const score = response?.score || 0;  // Le mark calculé par le composant (0 à 1)
  const maxScore = response?.maxScore || 1;
  const isCorrect = score >= (maxScore * 0.99); // Considérer correct si score >= 99% (pour éviter les erreurs d'arrondi)

  console.log('📝 Processing answer FINAL:', {
    questionId: currentQuestion.value.el_ID,
    calculatedScore: score,
    maxScore: maxScore,
    isCorrect,
    evaluationResult: isCorrect ? '✅ CORRECT' : '❌ INCORRECT'
  });

  // Traiter la réponse avec le moteur CAT
  const result = catEngine.value.processAnswer(currentQuestion.value, isCorrect, response.answered, score);
  
  console.log('🔄 CAT engine result:', result);

  // Incrémenter l'index de question
  currentQuestionIndex.value++;

  // Vérifier si le test est terminé
  if (result.isCompleted) {
    finishTest();
    return;
  }

  // Obtenir la prochaine question
  const nextQuestion = catEngine.value.getNextQuestion();
  
  if (!nextQuestion) {
    console.warn('⚠️ No next question available, finishing test');
    finishTest();
    return;
  }

  // Mettre à jour la question actuelle
  currentQuestion.value = nextQuestion;
  
  console.log('➡️ Next question selected:', {
    questionId: nextQuestion.el_ID,
    newLevel: result.newLevel,
    questionsAnswered: result.questionsAnswered
  });
};

// Évalue si une réponse est correcte (fonction simplifiée)
const evaluateAnswer = (question, answer) => {
  console.log('🔍 Evaluating answer:', {
    questionType: question.el_Type,
    questionId: question.el_ID,
    answer: answer,
    options: question.options?.map(opt => ({ id: opt.id, text: opt.text, isCorrect: opt.isCorrect }))
  });

  // Pour les questions à choix multiples
  if (question.el_Type === 'choice' && question.options) {
    const correctOptions = question.options.filter(opt => opt.isCorrect); // ✅ CORRIGÉ: opt.isCorrect au lieu de opt.correct
    
    console.log('✅ Correct options found:', correctOptions.map(opt => ({ id: opt.id, text: opt.text })));
    
    if (correctOptions.length === 1) {
      // Une seule bonne réponse
      const isAnswerCorrect = correctOptions[0].id === answer;
      console.log('🎯 Single choice evaluation:', { 
        expectedId: correctOptions[0].id, 
        givenAnswer: answer, 
        isCorrect: isAnswerCorrect 
      });
      return isAnswerCorrect;
    } else {
      // Plusieurs bonnes réponses possibles
      const isAnswerCorrect = Array.isArray(answer) && 
             answer.length === correctOptions.length &&
             correctOptions.every(opt => answer.includes(opt.id));
      console.log('🎯 Multiple choice evaluation:', { 
        expectedIds: correctOptions.map(opt => opt.id), 
        givenAnswers: answer, 
        isCorrect: isAnswerCorrect 
      });
      return isAnswerCorrect;
    }
  }
  
  // Pour les questions à réponse courte (évaluation basique)
  if (question.el_Type === 'shortAnswer') {
    // Ici on devrait avoir une logique plus sophistiquée
    // Pour l'instant, on considère que toute réponse non vide est correcte
    return answer && answer.toString().trim().length > 0;
  }
  
  // Pour les autres types, évaluation basique
  return answer !== undefined && answer !== null && answer !== '';
};

// Termine le test et génère le rapport final
const finishTest = () => {
  if (!catEngine.value) return;
  
  // Générer le rapport final
  finalReport.value = catEngine.value.generateFinalReport();
  testCompleted.value = true;
  
  console.log('🎉 CAT Test completed:', finalReport.value);
  
  // Pour les tests CAT, on n'utilise pas lastPageReached mais testCompleted
  // Envoyer les résultats sans déclencher la LastPage classique
  afterCATTest();
};

// Event handlers
function handleLockQuestion(questionId) {
  lockedQuestions.value.add(questionId);
}

// Fonction spécifique pour la fin des tests CAT (ne déclenche pas lastPageReached)
async function afterCATTest() {
  stopTimer.value = true;
  // ❌ PAS de lastPageReached.value = true; pour les tests CAT
  
  // En mode preview, ne pas sauvegarder les résultats
  if (props.previewMode) {
    console.log('🎬 Mode preview - Skip saving results');
    savingStatus.value = 'success';
    return;
  }
  
  // Set saving status to show spinner
  savingStatus.value = 'saving';
  saveError.value = '';
  
  try {
    console.log('💾 Starting CAT result save process...');
    
    // Préparer les données de résultat avec le rapport CAT
    const resultData = {
      userId, 
      testId: testId.value, 
      publicationId, 
      groupId,
      owner: publicationStore.publicationData?.owner,
      catEngine: catEngine.value, // Passer le moteur CAT pour traitement
      testData: testData.value // Ajouter les données du test pour l'enrichissement des dimensions
    };
    
    await sendResultsToDatabase(resultData);
    await sessionManager?.endSession(false);

    // Incrémenter les tentatives
    await api.patch(`/publications/${publicationId}/increment-attempts`);
    
    console.log('✅ CAT results sent successfully');
    
    // Update saving status to success
    savingStatus.value = 'success';
    
  } catch (error) {
    console.error('❌ Error sending CAT results:', error);
    
    // Update saving status to error
    savingStatus.value = 'error';
    saveError.value = error.message || 'Unknown error occurred while saving results';
  }
}

// Fonction originale pour les tests linéaires (conservée si nécessaire)
async function afterTest() {
  stopTimer.value = true;
  lastPageReached.value = true;
  
  try {
    // Préparer les données de résultat avec le rapport CAT
    const resultData = {
      userId, 
      testId: testId.value, 
      publicationId, 
      groupId,
      owner: publicationStore.publicationData?.owner,
      catEngine: catEngine.value, // Passer le moteur CAT pour traitement
      testData: testData.value // Ajouter les données du test pour l'enrichissement des dimensions
    };
    
    await sendResultsToDatabase(resultData);
    await sessionManager?.endSession(false);

    // Incrémenter les tentatives
    await api.patch(`/publications/${publicationId}/increment-attempts`);
    console.log('✅ CAT results sent successfully');
  } catch (error) {
    console.error('❌ Error sending CAT results:', error);
  }
}

const onTimeUp = async () => {
  console.log('⏰ Time up for CAT test');
  finishTest();
};

// Computed properties pour l'affichage des statistiques CAT
const levelValidationStatus = computed(() => {
  if (!catEngine.value) return [];
  
  const breakdown = catEngine.value.generateLevelBreakdown();
  
  return breakdown.map(level => ({
    level: level.level,
    correctCount: level.successes,
    totalAttempts: level.questionsAttempted,
    validated: level.successes >= Math.ceil(catEngine.value.confirmationThreshold / catEngine.value.levels.length)
  }));
});

// Helper functions pour le debug panel

const getCompletionStatus = () => {
  if (!catEngine.value) return 'Initializing';
  if (catEngine.value.isTestCompleted()) {
    const reason = catEngine.value.completionReason;
    switch (reason) {
      case 'confirmation_threshold_reached': return 'Level Confirmed';
      case 'failure_threshold_reached': return 'Minimum Level';
      case 'no_more_questions': return 'All Questions Used';
      default: return 'Completed';
    }
  }
  return 'In Progress';
};

const getCompletionStatusClass = () => {
  if (!catEngine.value) return 'text-muted';
  if (catEngine.value.isTestCompleted()) {
    const reason = catEngine.value.completionReason;
    switch (reason) {
      case 'confirmation_threshold_reached': return 'text-success';
      case 'failure_threshold_reached': return 'text-warning';
      case 'no_more_questions': return 'text-info';
      default: return 'text-success';
    }
  }
  return 'text-primary';
};

const getLevelBreakdown = () => {
  if (!catEngine.value) return [];
  return catEngine.value.generateLevelBreakdown();
};

const getDifficultyLabel = (index) => {
  const labels = ['Beginner', 'Easy', 'Medium', 'Hard', 'Expert', 'Master'];
  return labels[index] || `Level ${index + 1}`;
};

const getSuccessPercentage = (levelData) => {
  if (levelData.questionsAttempted === 0) return 0;
  return (levelData.successes / levelData.questionsAttempted) * 100;
};

const getFailurePercentage = (levelData) => {
  if (levelData.questionsAttempted === 0) return 0;
  return (levelData.failures / levelData.questionsAttempted) * 100;
};

const getPerformancePattern = () => {
  if (!catEngine.value) return 'Unknown';
  
  const state = catEngine.value.getCurrentState();
  const successRate = state.questionsAnswered > 0 ? (state.totalSuccesses / state.questionsAnswered) : 0;
  
  if (successRate >= 0.8) return 'Excellent';
  if (successRate >= 0.6) return 'Good';
  if (successRate >= 0.4) return 'Average';
  return 'Needs Improvement';
};

const getWarningMessage = () => {
  if (!catEngine.value) return null;
  
  const levelStats = currentLevelStats.value;
  
  // Warning si proche du seuil d'élimination au niveau 1
  if (currentLevel.value === 1) {
    const remaining = levelStats.failureThreshold - levelStats.failures;
    if (remaining <= 1 && remaining > 0) {
      return `⚠️ Warning: Only ${remaining} mistake(s) allowed at minimum level before elimination!`;
    }
  }
  
  // Info si proche de la confirmation du niveau actuel
  const remaining = levelStats.confirmationThreshold - levelStats.successes;
  if (remaining <= 2 && remaining > 0) {
    return `🎯 Almost there! Only ${remaining} more correct answer(s) needed to confirm level ${currentLevel.value}.`;
  }
  
  return null;
};

// Initialisation
onMounted(async () => {
  // En mode preview, utiliser les props directement
  if (props.previewMode && props.testData) {
    console.log('🎬 Mode CAT preview activé');
    testData.value = props.testData;
    
    // Initialiser le moteur CAT avec les données fournies
    try {
      catEngine.value = new CATEngine(testData.value);
      currentQuestion.value = catEngine.value.getNextQuestion();
      
      if (!currentQuestion.value) {
        throw new Error('Unable to get first question from CAT engine');
      }
      
      console.log('✅ CAT engine initialized in preview mode:', {
        startingLevel: catEngine.value.currentLevel,
        totalLevels: catEngine.value.levels.length,
        totalElements: testData.value.elements.length,
        firstQuestion: currentQuestion.value.el_ID
      });
      
      catReady.value = true;
    } catch (error) {
      console.error('❌ Error initializing CAT in preview mode:', error);
    }
    
    return; // Skip normal initialization in preview mode
  }
  
  // Mode normal - comportement existant
  await fetchCATData();
  
  if (publicationId) {
    await publicationStore.fetchPublication(publicationId);
  }

  const publicationAccess = publicationStore.publicationData?.access;

  sessionManager = useSessionManager(
    testId,
    publicationId,
    groupId,
    userId,
    publicationAccess
  );

  if (token && userId) {
    await sessionManager.startSession();
  }
});

onUnmounted(() => {
  // Cleanup si nécessaire
});
</script>

<style scoped>
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  text-align: center;
}

.adaptive-test-wrapper {
  width: 100%;
  min-height: 100vh;
}

.preview-banner {
  background: linear-gradient(135deg, #ffeaa7, #fdcb6e);
  color: #2d3436;
  padding: 12px 20px;
  text-align: center;
  font-weight: 500;
  border-bottom: 3px solid #e17055;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.adaptive-test-container {
  max-width: 100%;
  margin: 0 auto;
  padding: 30px;
  flex: 1;
}

.cat-progress-bar {
  margin-top: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  border-left: 4px solid #17a2b8;
}

.cat-status {
  display: flex;
  justify-content: center;
  gap: 10px;
}

.cat-navigation-inline {
  text-align: center;
  margin-top: 20px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

/* Competency Validation Status - Affiché pendant le test */
.validation-status {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.level-badge {
  text-align: center;
  padding: 15px;
  border-radius: 8px;
  position: relative;
  transition: all 0.3s ease;
}

.level-badge.validated {
  background: #d4edda;
  border: 2px solid #28a745;
}

.level-badge.in-progress {
  background: #fff3cd;
  border: 2px solid #ffc107;
}

.level-badge.not-started {
  background: #f8f9fa;
  border: 2px solid #dee2e6;
}

.level-number {
  font-size: 1.2em;
  font-weight: bold;
  margin-bottom: 5px;
}

.level-progress {
  font-size: 0.9em;
  color: #6c757d;
}

.validation-icon {
  position: absolute;
  top: -5px;
  right: -5px;
  color: #28a745;
  background: white;
  border-radius: 50%;
  font-size: 1.2em;
}

/* CAT Debug Panel Styles */
.cat-debug-panel {
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.debug-header {
  text-align: center;
  margin-bottom: 20px;
  border-bottom: 2px solid #dee2e6;
  padding-bottom: 10px;
}

.debug-header h5 {
  color: #495057;
  margin: 0;
}

.debug-card {
  background: white;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #dee2e6;
  height: 100%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.debug-card h6 {
  color: #6c757d;
  margin-bottom: 15px;
  font-size: 0.9em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  padding: 5px 0;
  border-bottom: 1px solid #f8f9fa;
}

.status-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.status-item .label {
  font-size: 0.9em;
  color: #6c757d;
  font-weight: 500;
}

.status-item .value {
  font-weight: 600;
  font-size: 1em;
}

.threshold {
  color: #6c757d;
  font-size: 0.9em;
  margin-left: 5px;
}

.level-badge-mini {
  background: #007bff;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8em;
  font-weight: bold;
}

.level-1 { background: #dc3545; }
.level-2 { background: #fd7e14; }
.level-3 { background: #ffc107; color: #212529; }
.level-4 { background: #20c997; }
.level-5 { background: #0dcaf0; }

.completion-indicator .value {
  font-weight: bold;
  text-transform: uppercase;
  font-size: 0.9em;
  letter-spacing: 0.5px;
}

/* Level Breakdown Visualization */
.level-breakdown {
  margin-top: 20px;
  background: white;
  border-radius: 8px;
  padding: 15px;
  border: 1px solid #dee2e6;
}

.level-breakdown h6 {
  color: #6c757d;
  margin-bottom: 15px;
  font-size: 0.9em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.levels-grid {
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
}

.level-bar-container {
  flex: 1;
  min-width: 120px;
  text-align: center;
}

.level-header {
  margin-bottom: 8px;
}

.level-number {
  font-weight: bold;
  color: #495057;
  display: block;
  font-size: 0.9em;
}

.level-difficulty {
  font-size: 0.75em;
  color: #6c757d;
  display: block;
}

.level-bar {
  height: 20px;
  background: #e9ecef;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  margin-bottom: 8px;
}

.success-bar {
  height: 100%;
  background: linear-gradient(90deg, #28a745, #20c997);
  position: absolute;
  left: 0;
  top: 0;
  transition: width 0.3s ease;
}

.failure-bar {
  height: 100%;
  background: linear-gradient(90deg, #dc3545, #fd7e14);
  position: absolute;
  right: 0;
  top: 0;
  transition: width 0.3s ease;
}

.level-stats {
  font-size: 0.75em;
  line-height: 1.2;
}

.level-stats span {
  display: block;
  margin: 2px 0;
}

.warning-section {
  margin-top: 15px;
}

.warning-section .alert {
  margin: 0;
  font-size: 0.9em;
  border-left: 4px solid #ffc107;
}

.test-completion-message {
  width: 100%;
  margin-top: 20px;
}

.test-completion-message .alert {
  border: 2px solid #28a745;
  background: linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%);
}
</style>